import React, { FC, useEffect } from 'react';
import moment from 'moment/moment';
import { FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm, UseFormOptions } from 'react-hook-form';

import { fc } from 'i18n';
import { IDraftForm, IDraftPayload } from 'types/tep';
import { close as closeSign } from 'assets/icons';
import Button from 'core/react/general/form/Button';
import { Preloader } from 'components/atoms/preloader';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { draftModalSelector } from 'core/redux/selectors/tep';
import { ModalWrapper } from 'components/molecules/modal-wrapper';
import { useDraftColumns } from 'hooks/tep/leases/useDraftColumns';
import { InfoTable } from 'components/organisms/tables/info-table';
import { actions as leasesActions } from 'core/redux/api/veta/tep/leases';

import styles from './styles.module.css';

const FORM_OPTIONS: UseFormOptions<IDraftForm> = {
  mode: 'onChange',
};

export const DraftModal: FC = () => {
  const dispatch = useDispatch();

  const { isOpen, isLoading, error, selected } = useSelector(draftModalSelector);

  const formMethods = useForm<IDraftForm>(FORM_OPTIONS);

  const { control, setValue, handleSubmit } = formMethods;

  const { fields } = useFieldArray({ control, name: 'leases', keyName: 'fieldId' });

  const columns = useDraftColumns({ isMultiple: fields.length > 1 });

  const onSubmit = handleSubmit(({ leases }: IDraftForm) => {
    const drafts: Record<string, IDraftPayload> = {};

    leases.forEach((lease, index) => {
      const { company, email, plateNumber, startOn, endOn } = lease;

      drafts[fields[index].id] = {
        companyId: company?.value,
        email,
        plateNumber,
        startOn: moment(startOn).format('YYYY-MM-DD'),
        endOn: moment(endOn).format('YYYY-MM-DD'),
      };
    });

    dispatch(leasesActions.draft.request({ data: { leases: drafts } }));
  });

  const setLeases = () => {
    const defaultFields = { plateNumber: '' };

    const formLeases = selected.map(({ id, device }) => ({
      id,
      name: device.name,
      uri: device.uri,
      ...defaultFields,
    }));

    setValue('leases', formLeases);
  };

  const close = () => dispatch(leasesActions.close());

  useEffect(() => {
    if (isOpen) {
      setLeases();
    }
  }, [isOpen]);

  return (
    <ModalWrapper>
      <FormProvider {...formMethods}>
        <form className={styles.content} onSubmit={onSubmit}>
          <span className={styles.close} onClick={close}>
            <SvgWrapper width={26} height={26} details={closeSign} />
          </span>
          <div className={styles.title}>{fc('draft')}</div>
          {/*@ts-ignore*/}
          <InfoTable isLoading={false} height={240} columns={columns} data={fields} />
          <div className={styles.totalCount}>{`Total No. Of Entries : ${fields.length}`}</div>
          <div className={styles.footer}>
            <div className={styles.error}>{error || ''}</div>
            <Button className={`${styles.submit} active`} type="submit">
              <span>{fc('draft')}</span>
            </Button>
          </div>
        </form>
        {isLoading && <Preloader />}
      </FormProvider>
    </ModalWrapper>
  );
};
